import { useState, useCallback, KeyboardEvent, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { messageApi } from 'src/services/whatsapp'
import { Button } from '../button'
import { Input } from '../input'
import {
  PaperAirplaneIcon,
  PhotoIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/style'
import { Spinner } from '../spinner'

interface MessageInputProps {
  onSend: (message: string, mediaUrls?: string[]) => void
  isLoading?: boolean
  disabled?: boolean
  placeholder?: string
}

/**
 * Free-form composer. Only valid while the conversation's 24-hour window is
 * open, so it is rendered by `MessageComposer`, which owns that decision and
 * surfaces send/balance errors above this input.
 */
export const MessageInput = ({
  onSend,
  isLoading = false,
  disabled = false,
  placeholder,
}: MessageInputProps) => {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('chat.typePlaceholder')
  const [message, setMessage] = useState('')
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { mutate: uploadMedia, isPending: isUploading } = useMutation({
    mutationFn: (file: File) => messageApi.csc.uploadMedia({ file }),
    onSuccess: (item) => setPendingImageUrl(item.url),
    onError: () => {
      setUploadError(t('chat.uploadFailed'))
      setImagePreview((current) => {
        if (current) URL.revokeObjectURL(current)
        return null
      })
    },
  })

  const handleSend = useCallback(() => {
    const trimmedMessage = message.trim()
    if (!isLoading && !disabled && !isUploading && (trimmedMessage || pendingImageUrl)) {
      const mediaUrls = pendingImageUrl ? [pendingImageUrl] : undefined
      onSend(trimmedMessage, mediaUrls)
      setMessage('')
      setPendingImageUrl(null)
      setImagePreview(null)
    }
  }, [message, isLoading, disabled, isUploading, pendingImageUrl, onSend])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      // Reset the input so the same file can be selected again
      e.target.value = ''
      if (!file) return

      setUploadError(null)
      // Preview locally while the file uploads; the media URL sent to the
      // server is the stored one the upload returns, since Twilio fetches it
      // itself and cannot read a browser object URL.
      setImagePreview((current) => {
        if (current) URL.revokeObjectURL(current)
        return URL.createObjectURL(file)
      })
      setPendingImageUrl(null)
      uploadMedia(file)
    },
    [uploadMedia],
  )

  const handleRemoveImage = useCallback(() => {
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setPendingImageUrl(null)
    setImagePreview(null)
    setUploadError(null)
  }, [imagePreview])

  const canSend = (message.trim() || pendingImageUrl) && !isLoading && !disabled && !isUploading

  return (
    <div className="bg-surface p-4">
      {uploadError && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-800">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span>{uploadError}</span>
        </div>
      )}

      {/* Image preview */}
      {imagePreview && (
        <div className="mb-2 flex items-center gap-2">
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt={t('chat.attachmentPreview')}
              className={cn([
                'max-h-20 max-w-32 rounded-md border border-border object-contain',
                isUploading && 'opacity-50',
              ])}
            />
            {isUploading && (
              <span className="absolute inset-0 flex items-center justify-center">
                <Spinner size="sm" />
              </span>
            )}
            <Button
              type="button"
              variant="secondary"
              iconOnly
              size="sm"
              shape="circle"
              onClick={handleRemoveImage}
              className="absolute -right-1.5 -top-1.5"
              aria-label={t('chat.removeAttachment')}
            >
              <XMarkIcon className="h-3 w-3" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          aria-label={t('chat.attachImage')}
          data-testid="message-media-input"
        />

        {/* Attach image button */}
        <Button
          type="button"
          variant="ghost"
          tone="neutral"
          iconOnly
          shape="square"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isLoading || isUploading}
          className="flex-shrink-0"
          aria-label={t('chat.attachImage')}
          title={t('chat.attachImage')}
        >
          <PhotoIcon className="h-5 w-5" aria-hidden="true" />
        </Button>

        <Input
          type="text"
          placeholder={resolvedPlaceholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isLoading}
          className="flex-1"
        />
        <Button
          onClick={handleSend}
          disabled={!canSend}
          isLoading={isLoading}
          type="button"
          data-testid="message-send"
          aria-label={t('chat.send')}
        >
          <PaperAirplaneIcon className="h-5 w-5" />
        </Button>
      </div>
    </div>
  )
}
